import { createSerialDriver, type SqlDriver, type SqlValue } from '@fd/sync-engine';

type Pending = { resolve: (value: unknown) => void; reject: (error: Error) => void };

const opened = new Map<string, Promise<{ driver: SqlDriver; persistent: boolean }>>();

/**
 * SqlDriver backed by SQLite WASM running in a worker (see sqlite.worker.ts). One worker per
 * database for the page's lifetime: OPFS access handles are exclusive, so opening the same file
 * twice (React StrictMode runs effects twice in development) would fail.
 */
export function openBrowserDatabase(name: string): Promise<{ driver: SqlDriver; persistent: boolean }> {
  let existing = opened.get(name);
  if (!existing) {
    existing = openWorker(name);
    opened.set(name, existing);
  }
  return existing;
}

async function openWorker(name: string): Promise<{ driver: SqlDriver; persistent: boolean }> {
  const worker = new Worker(new URL('./sqlite.worker.ts', import.meta.url), { type: 'module' });
  const pending = new Map<number, Pending>();
  let nextId = 1;
  worker.onmessage = (event: MessageEvent<{ id: number; ok: boolean; result?: unknown; error?: string }>) => {
    const entry = pending.get(event.data.id);
    if (!entry) return;
    pending.delete(event.data.id);
    if (event.data.ok) entry.resolve(event.data.result);
    else entry.reject(new Error(event.data.error ?? 'SQLite error'));
  };
  const call = <T>(message: Record<string, unknown>): Promise<T> =>
    new Promise<T>((resolve, reject) => {
      const id = nextId++;
      pending.set(id, { resolve: resolve as (value: unknown) => void, reject });
      worker.postMessage({ id, ...message });
    });

  const result = await call<{ persistent: boolean }>({ kind: 'open', name });
  const driver = createSerialDriver({
    exec: (sql) => call<void>({ kind: 'exec', sql }),
    run: (sql, params = []) => call<void>({ kind: 'run', sql, params: params as SqlValue[] }),
    all: <T>(sql: string, params: readonly SqlValue[] = []) => call<T[]>({ kind: 'all', sql, params: params as SqlValue[] }),
    runMany: (statements) => call<void>({ kind: 'many', statements }),
  });
  return { driver, persistent: result.persistent };
}
